import { mutationOptions, queryOptions } from '@tanstack/react-query';

import { API_SERVICE } from '@/constants/api.constant';
import { GET, GET_FILE, POST } from '@/lib/fetch';
import { useDialogStore } from '@/store';
import type {
	DocumentAttachmentResponse,
	DocumentNdaResponse,
	PrivyCheckOtpResponse
} from '@/types/approval-document/approval-document';
import {
	type DataTableResponse,
	ErrorApiResponse,
	type SuccessResponse
} from '@/types/response';

import type { DataTableQuery } from '../../types/request';

export const approvalDocumentQueries = {
	keys: () => 'approvalDocuments' as const,
	listDocument: (query: DataTableQuery<{ nda: boolean }>) =>
		queryOptions<DataTableResponse<DocumentNdaResponse>, ErrorApiResponse>({
			queryKey: [approvalDocumentQueries.keys(), 'list', query],
			queryFn: () =>
				GET({
					service: API_SERVICE.API,
					path: '/document-nda',
					query
				})
		}),
	listAttachment: (id: string) =>
		queryOptions<DataTableResponse<DocumentAttachmentResponse>, ErrorApiResponse>(
			{
				queryKey: [approvalDocumentQueries.keys(), 'list', 'attachment', id],
				queryFn: () =>
					GET({
						service: API_SERVICE.API,
						path: `/document-nda/${id}/attachment`
					})
			}
		),
	viewPdf: (url: string) =>
		queryOptions<Blob, ErrorApiResponse>({
			queryKey: [approvalDocumentQueries.keys(), 'view', url],
			queryFn: () =>
				GET_FILE({
					path: '/document-nda/preview',
					service: API_SERVICE.API,
					query: { url }
				})
		}),
	checkOtp: (guidKey: string) =>
		queryOptions<SuccessResponse<PrivyCheckOtpResponse>, ErrorApiResponse>({
			queryKey: [approvalDocumentQueries.keys(), 'checkOtp', guidKey],
			queryFn: () =>
				POST({
					path: `/document-nda/${guidKey}/otp`,
					service: API_SERVICE.API
				})
		})
};

export const approvalDocumentMutations = {
	checkOtp: (guidKey: string) =>
		mutationOptions<SuccessResponse<PrivyCheckOtpResponse>, ErrorApiResponse>({
			mutationFn: () =>
				POST({
					path: `/document-nda/${guidKey}/otp`,
					service: API_SERVICE.API
				}),
			mutationKey: ['checkOtp', guidKey],
			meta: {
				invalidates: [approvalDocumentQueries.checkOtp(guidKey).queryKey]
			}
		}),
	validateDocument: (guidKey: string) => {
		const onOpen = useDialogStore((s) => s.onOpenChange);

		return mutationOptions<undefined, ErrorApiResponse, string>({
			mutationFn: (otpCode) =>
				POST(
					{
						path: `/document-nda/${guidKey}/validate`,
						service: API_SERVICE.API
					},
					{ body: { OTPCode: otpCode } }
				),
			onMutate: () => onOpen(false)
		});
	}
};
